import {CanOrder} from '../../CanOrder';
import {OnDestroy, OnInit} from '@angular/core';
import {AppStatus} from '../../@models/app-status';
import * as _ from 'lodash';
import {ProductDetailComponent} from '../../modals/product-detail/product-detail.component';
import {MatDialog}              from '@angular/material/dialog';
import {MenuPage}               from '../../../shared-gen/Model/Menus/MenuPage';
import {Menu}                   from '../../../shared-gen/Model/Menus/Menu';
import {ProductBase}            from '../../../shared-gen/Model/Products/ProductBase';
import {Choice}                 from '../../../shared-gen/Model/Products/Choice';
import {Product}                from '../../../shared-gen/Model/Products/Product';
import {VmcState}               from '../../../shared-gen/Vending/Status/VmcState';
import {DeviceStatusFun}        from '../../../shared/device/device-status.fun';
import {AppStatusFun}           from '../../@models/app-status.fun';
import {AppService}  from '../../services/app.service';
import { TimeGuard } from "../../../../../Shared/shared/utils/time-guard";
import { formatIoCurrency } from "../../../shared/pipes/currency.pipe";

export class MenuPageBase extends CanOrder implements OnInit, OnDestroy {

	currentPage: MenuPage = { Name: "", Columns: 1, Cells: [] };
	currentPageNumber: number = 0;

	private timeGuard = new TimeGuard();

	// Remaining number of products per product ID
	// May also be 0 if HACCP condition disables the product
	productsRemainingCount: {[productID: string]: number} = {};

	constructor(app: AppService,
	            protected dialogService: MatDialog) {
		super(app);
	}

	async ngOnInit() {
		await super.ngOnInit();
		// Reset selected button
		this.app.status.setSelectedProduct(null);
	}

	onStatusChanged(status: AppStatus) {
		console.log("onStatusChanged");
		super.onStatusChanged(status);

		const vending = status.deviceStatus.VendingStatus;

		// Update menu, if needed
		const newMenuJson = JSON.stringify(status.menuOriginal);
		if (newMenuJson != this.lastMenuJson) {
			this.lastMenuJson = newMenuJson;
			status.menu = _.cloneDeep(status.menuOriginal);
			this.menu = this.buildMenu(status.menu);
			if (this.menu.Pages) {
				this.currentPage = this.menu.Pages[0];
			}
			this.onMenuChanged();
		}
		// Update product availibility by filling level and HACCP condition
		this.productsRemainingCount = AppStatusFun.getProductsRemainingCount(status);
	}
	private lastMenuJson = "";

	/**
	 * Called, when the menu was changed.
	 */
	onMenuChanged() {
	}

	buildMenu(menu: Menu): Menu {
		const name = this.translate('🌐App.VM.AllProducts');

		// Create start page
		if (menu.Pages.length > 1 && menu.AutoStartPage) {
			const startpage = this.app.status.getStartPage();
			startpage.Name = name;
			if (menu.Pages[0].Name === name) {
				menu.Pages[0] = startpage;
			} else {
				menu.Pages.unshift(startpage);
			}
		}

		// Scrollable pages: duplicate product list to have enough
		// products for scrolling (remember this in flag "__duplicated")
		menu.Pages.forEach(page => {
			if (page.AutoScroll && page["__duplicated"] == null) {
				page["__duplicated"] = true;
				// Remove empty cells
				page.Cells = page.Cells.filter(it => it != null);
				// Duplicate (or even more often) so that we can scroll without holes
				const minItemsCount = page.Columns * 4;
				const origCells = _.cloneDeep(page.Cells);
				while (page.Cells.length < minItemsCount)
					page.Cells.push(... _.cloneDeep(origCells));
				console.log("Page " + page.Name + ": " + page.Cells.length);
			}
		});

		return menu;
	}

	hasInstanceOfChoice(product: ProductBase, choice: Choice): boolean {
		return product.Instances.findIndex(it => it.ID === `${product.ID}-${choice.ID}`) !== -1;
	}

	getInstanceOfChoice(product: ProductBase, choice: Choice): Product {
		if (choice === null) {
			return product.Instances[0];
		}
		return product.Instances.find(it => it.ID === `${product.ID}-${choice.ID}`);
	}

	choiceAvailable(base: ProductBase, choice: Choice): boolean {
		const product = this.getInstanceOfChoice(base, choice);
		return this.productIsAvailable(product);
	}

	async orderByChoice(productBase: ProductBase, choice: Choice, event: Event): Promise<boolean> {
		event.stopPropagation();
		this.reportActivity();
		//TODO wie funktionieren zukünftig verschachtelte optionen?
		const instance = this.getInstanceOfChoice(productBase, choice);
		if (!instance) {
			return false;
		}
		//TODO add try/catch to handle cart maximum
		await this.orderProductIfAvailable(productBase.Instances[0], null);
	}

	async openOptionsPageOrOrder(productBase: ProductBase, event) {
		if (productBase.Options && productBase.Options.length > 0) {
			this.app.status.setSelectedProduct(productBase);
			this.app.nav.openPageAndReportUserActivity(`/${this.theme}/options`);
		} else {
			await this.orderProductIfAvailable(productBase.Instances[0], event.target);
		}
	}

	async orderProductIfAvailable(product: Product, clickedElement: Element | null) {
		// Product available?
		if (!this.productsRemainingCount[product.ID] || this.isCartFull()) {
			// Not available (e.g. sold out or cart is full).
			return;
		}
		else if (false === this.timeGuard.try(200)) {
			// Allow next click only 200 milliseconds later
			// (to avoid double orderings because of fast double clicks)
		}
		else {
			// Available. Order it.
			this.playProductAddTransition(product.ID, clickedElement);
			await this.order(product);
		}
	}

	showPage(page: MenuPage) {
		this.currentPage = page;
	}

	getPriceOfChoice(product: ProductBase, choice: Choice): string {
		const instance = this.getInstanceOfChoice(product, choice);
		let price = this.getPrice(instance);
		return formatIoCurrency(price / 100, this.app.getCurrency());
	}

	choiceHasDeposit(product: ProductBase, choice: Choice): boolean {
		const instance = this.getInstanceOfChoice(product, choice);
		let deposit = this.getDeposit(instance);
		return deposit > 0;
	}

	getDepositOfChoice(product: ProductBase, choice: Choice): string {
		const instance = this.getInstanceOfChoice(product, choice);
		let deposit = this.getDeposit(instance);
		return formatIoCurrency(deposit / 100, this.app.getCurrency());
	}

	/*openInfo(product: ProductBase) {
		this.appStatusService.setSelectedProduct(product);
		this.navigator.openPageAndReportUserActivity(`/${this.theme}/options`);
	}*/

	openInfo(product: ProductBase, event) {
		event.preventDefault();
		event.stopPropagation();

		const modal = this.dialogService.open(
			ProductDetailComponent, {
				hasBackdrop: true,
				panelClass: 'cdk-overlay-pane--no-padding-inner'
			}
		);
		modal.componentInstance.product = product;
		const onClosed = modal.afterClosed().subscribe(() => {
			this.onInfoClosed();
			onClosed.unsubscribe();
		});
	}

	/**
	 * This method is called when the info dialog was closed.
	 */
	onInfoClosed() {
	}


	async reportActivity() {
		if (DeviceStatusFun.getVmcState(this.deviceStatus) === VmcState.Inactive) {
			console.log('reset inactive timer');
			await this.app.status.reportUserActivity();
		}
	}

	playProductAddTransition(id, clickedElement: Element | null) {
		console.log("clicked: " , clickedElement);

		// Find the clicked product HTML element in the parent chain of the given clicked element,
		// looking for a "data-prd-id" child. If not possible, try to find the product anywhere in the document.
		let product: Element = null;
		let e = clickedElement;
		while (e != null && !product) {
			console.log("trying here: ", e, e["dataset"]);
			if (e["dataset"]["prdId"]) // data-prd-id => prdId (automatic by browser)
				product = e;
			e = e.parentElement;
		}
		if (product == null)
			product = document.querySelectorAll('[data-prd-id="' + id + '"]')[0];

		let cartItems = this.deviceStatus.VendingStatus.Cart.Items.length;
		if (product && !document.querySelector('#prd-id__' + id + '--shadow--pos-' + cartItems)) {
			let prdPos = {
				top: (product as any).offsetTop,
				left: (product as any).offsetLeft,
				width: (product as any).offsetWidth,
				height: (product as any).offsetHeight
			};
			let productImage = product.querySelectorAll('figure img')[0];
			let origSize = {
				w: Math.floor(productImage.getBoundingClientRect().width),
				h: Math.floor(productImage.getBoundingClientRect().height)
			};
			let wrapperScrolled = document.querySelectorAll('.overflow-wrapper')[0].scrollTop;
			let shadowProduct = (productImage.cloneNode(true) as any);
			shadowProduct.setAttribute('id', 'prd-id__' + id + '--shadow--pos-' + cartItems);
			shadowProduct.className = 'shadow-product-image';
			shadowProduct.style.top = 'calc(' + prdPos.top + 'px' + ' + 2.0833333333vw - ' + wrapperScrolled + 'px)';
			shadowProduct.style.left = 'calc(' + prdPos.left + 'px' + ' + 2.0833333333vw)';
			shadowProduct.style.width = origSize.w + 'px';
			shadowProduct.style.height = origSize.h + 'px';

			let terminal = document.querySelectorAll('terminal')[0];
			let cart = terminal.querySelectorAll('cart')[0];
			let firstEmptyCartItem;

			firstEmptyCartItem = cart.querySelectorAll('figure')[cartItems];
			if (firstEmptyCartItem) {
				(productImage as any).offsetParent.appendChild(shadowProduct);
				//ToDo bessere Lösung ohne verschachtele Timeouts überlegen
				setTimeout(() => {

					let posTop = (terminal as any).offsetTop + (cart as any).offsetTop + (firstEmptyCartItem as any).offsetTop,
						posLeft = (terminal as any).offsetLeft + (cart as any).offsetLeft + (firstEmptyCartItem as any).offsetLeft;
					//posLeft = (terminal as any).offsetLeft + (cart as any).offsetLeft;
					shadowProduct.style.top = 'calc(' + posTop + 'px' + ' + 0.2604166667vw)';
					shadowProduct.style.left = 'calc(' + posLeft + 'px' + ' + 0.2604166667vw)';
					shadowProduct.style.width = 'calc(' + (firstEmptyCartItem as any).offsetWidth + 'px' + ' - (2 * 0.2604166667vw))';
					shadowProduct.style.height = 'calc(' + (firstEmptyCartItem as any).offsetHeight + 'px' + ' - (2 * 0.2604166667vw))';
					shadowProduct.style.opacity = 1;
					setTimeout(() => {
						shadowProduct.className = shadowProduct.className + ' shadow-product-image--fade-out';
						setTimeout(() => {
							shadowProduct.parentNode.removeChild(shadowProduct);
						}, 1500);
					}, 1500); // Flying time of products to cart
				}, 200);
			}
		}
	}
}

